import React, { useRef } from 'react';
import { z } from 'zod/v3';
import { ChevronRight, Database, Upload, Warehouse } from 'lucide-react';
import { CocoEvent } from '@cotera/client/app/etc';
import { Assert } from '@cotera/utilities';
import { ModalPageView } from '@cotera/client/app/components/app';
import { Text } from '@cotera/client/app/components/ui';
import { cn } from '@cotera/client/v0/lib/utils';
import {
  ActionResult,
  ActionTrackingContext,
  ActionTrackMetadata,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { CreateDatasetAction } from './create-dataset.action';
import { UploadCsvDatasetAction } from './upload-csv-dataset.action';
import { CreateWarehouseDatasetAction } from './create-warehouse-dataset.action';
import { WarehouseDatasetSetupView } from './warehouse-dataset-setup';

const paramsSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('manual') }),
  z.object({
    mode: z.literal('csv'),
    file: z.custom<File>(
      (value) => typeof File !== 'undefined' && value instanceof File
    ),
  }),
  z.object({
    mode: z.literal('warehouse'),
    sourceQuery: z.string(),
    rowKeyColumns: z.array(z.string()),
    cadence: z.string(),
    name: z.string().optional(),
  }),
]);

type Params = z.infer<typeof paramsSchema>;

/** Only the modal-stack surface this action actually calls. */
type ModalManager = {
  push: (props: { view: () => React.ReactNode; onClose?: () => void }) => void;
  pop: () => void;
};

/**
 * Entry point for adding a dataset. Asks how to start — a blank manual dataset
 * or a CSV upload — then hands off to the action that owns that flow.
 *
 * The two ways of getting a dataset used to both live on the new-dataset page
 * (an empty manual dataset that could then import a CSV), but importing a CSV
 * into a not-yet-persisted dataset didn't work. Splitting the choice up front
 * means CSV goes straight through {@link UploadCsvDatasetAction}, which creates
 * the dataset from the file in one shot, and manual opens the new-dataset page.
 */
export class AddDatasetAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'New dataset';
  description = 'Add a dataset from scratch or a CSV';
  inputSchema = paramsSchema;
  group = 'Dataset';
  icon = 'dataset';

  constructor(
    private readonly createDatasetAction: CreateDatasetAction,
    private readonly uploadCsvAction: UploadCsvDatasetAction,
    private readonly createWarehouseAction: CreateWarehouseDatasetAction,
    private readonly modalManager: ModalManager
  ) {
    super();
  }

  applicable(_context: ApplicableContext): boolean {
    return true;
  }

  async execute(
    params: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    switch (params.mode) {
      case 'manual':
        return this.createDatasetAction.execute({}, context);
      case 'csv':
        return this.uploadCsvAction.execute({ file: params.file }, context);
      case 'warehouse':
        return this.createWarehouseAction.execute(
          {
            sourceQuery: params.sourceQuery,
            rowKeyColumns: params.rowKeyColumns,
            cadence: params.cadence,
            ...(params.name !== undefined && { name: params.name }),
          },
          context
        );
      default:
        return Assert.unreachable(params);
    }
  }

  ask(
    _params: Partial<Params>,
    _context: ExecuteContext
  ): { view: (handlers: AskHandlers) => void } {
    return {
      view: ({ onRespond, onCancel }) => {
        const respondManual = async () => {
          this.modalManager.pop();
          await onRespond({ mode: 'manual' });
        };
        const respondCsv = async (file: File) => {
          this.modalManager.pop();
          await onRespond({ mode: 'csv', file });
        };
        const respondWarehouse = async (params: {
          sourceQuery: string;
          rowKeyColumns: string[];
          name?: string;
          cadence: string;
        }) => {
          // Pops the setup view, then the picker beneath it.
          this.modalManager.pop();
          this.modalManager.pop();
          await onRespond({ mode: 'warehouse', ...params });
        };
        // Warehouse setup is its own step on top of the picker: connect a
        // warehouse if there is none, then enter the SELECT. Closing it drops
        // back to the picker rather than cancelling the whole flow.
        const pickWarehouse = () => {
          this.modalManager.push({
            view: () => (
              <WarehouseDatasetSetupView
                onSubmit={(params) => void respondWarehouse(params)}
              />
            ),
            onClose: () => this.modalManager.pop(),
          });
        };
        const cancel = async () => {
          this.modalManager.pop();
          await onCancel();
        };

        this.modalManager.push({
          view: () => (
            <AddDatasetPickerView
              onPickManual={respondManual}
              onPickCsv={respondCsv}
              onPickWarehouse={pickWarehouse}
            />
          ),
          onClose: () => void cancel(),
        });
      },
    };
  }

  track(
    params: ActionTrackingContext<Params, { t: string; message?: string }[]>
  ): ActionTrackMetadata | null {
    if (params.result.isErr()) {
      return null;
    }
    const resourceId = params.result.value.resourceId;
    // A cancelled ask resolves ok without ever running execute, so there is no
    // created dataset (and no `mode`) to report.
    if (resourceId === undefined || params.payload.mode === undefined) {
      return null;
    }
    return {
      event: CocoEvent.DatasetCreated,
      properties: { datasetId: resourceId, type: params.payload.mode },
    };
  }
}

type AskHandlers = {
  onRespond: (params: Params) => void | Promise<unknown>;
  onCancel: () => void | Promise<void>;
};

const AddDatasetPickerView: React.FC<{
  onPickManual: () => void | Promise<void>;
  onPickCsv: (file: File) => void | Promise<void>;
  onPickWarehouse: () => void;
}> = ({ onPickManual, onPickCsv, onPickWarehouse }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  return (
    <ModalPageView
      title="New dataset"
      subtitle="Choose how to start"
      className="px-4 py-4"
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file === undefined) {
            return;
          }
          void onPickCsv(file);
        }}
      />
      <div className="flex flex-col gap-2">
        <AddDatasetOption
          icon={<Database className="h-5 w-5" />}
          title="Blank dataset"
          description="Add columns and type in the values yourself."
          onClick={() => void onPickManual()}
        />
        <AddDatasetOption
          icon={<Upload className="h-5 w-5" />}
          title="Upload CSV"
          description="Create a dataset from the rows in a CSV file."
          onClick={() => fileInputRef.current?.click()}
        />
        <AddDatasetOption
          icon={<Warehouse className="h-5 w-5" />}
          title="Warehouse query"
          description="Seed a dataset from a SELECT against your connected warehouse."
          onClick={() => onPickWarehouse()}
        />
      </div>
    </ModalPageView>
  );
};

const AddDatasetOption: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
}> = ({ icon, title, description, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'flex items-center gap-3 px-4 py-3 border border-border/60 rounded-lg bg-card text-left',
      'hover:border-primary/30 hover:bg-accent/30 hover:shadow-sm transition-all cursor-pointer'
    )}
  >
    <div className="shrink-0 flex items-center justify-center w-9 h-9 rounded-md bg-muted/30 text-muted-foreground">
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <Text.P className="font-medium text-slate-800">{title}</Text.P>
      <p className="text-xs text-muted-foreground truncate">{description}</p>
    </div>
    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/50" />
  </button>
);
